import {
	ChevronLeftIcon,
	ChevronRightIcon,
	PlusIcon,
} from "@heroicons/react/24/outline";
import { format, isSameMonth } from "date-fns";
import { enUS, es, fr, he, ru } from "date-fns/locale";
import type { JourneyTaskOccurrence } from "../../../domain/track/journeyTaskOccurrenceAdapter";
import type { TwoWeekWindow } from "../../../hooks/track/useTwoWeekWindow";
import { useClientTranslations } from "../../../lib/i18n/client";
import type { Event } from "../../../types/event";
import { EventLegend } from "../EventLegend";
import { TwoWeekCalendar } from "../TwoWeekCalendar";
import styles from "./TrackOverview.module.css";

const LOCALE_MAP = { en: enUS, es, fr, he, ru } as const;

/** M011 S04 T03 — union of items that can appear on the calendar. */
type CalendarItem = Event | JourneyTaskOccurrence;

interface TrackOverviewProps {
	events: Event[] | undefined;
	/**
	 * M011 S04 T03 — journey task occurrences threaded through to the calendar.
	 * Wired by TrackPage in T04 from useJourneyProgress.
	 */
	journeyOccurrences?: JourneyTaskOccurrence[];
	window: TwoWeekWindow;
	onDayTap: (dayIso: string, itemsForDay: CalendarItem[]) => void;
	/** Phase 37 wiring (D-16) — TrackPage owns AddEventSheet open state. */
	onAddEvent?: () => void;
	/** Phase 38 D-12 — propagated from TrackPage's deep-link consumer. */
	focusDayIso?: string | null;
	/** ISO yyyy-MM-dd of the active/selected day; drives the highlight border. */
	selectedDayIso?: string | null;
}

export function TrackOverview({
	events,
	journeyOccurrences,
	window,
	onDayTap,
	onAddEvent,
	focusDayIso,
	selectedDayIso,
}: TrackOverviewProps) {
	const { t, locale } = useClientTranslations("track");
	const dateFnsLocale = LOCALE_MAP[locale as keyof typeof LOCALE_MAP] ?? enUS;

	// CAL-02: month label(s) — joined with "/" when window spans two months
	const firstMonth = format(window.windowStart, "MMMM", {
		locale: dateFnsLocale,
	});
	const lastMonth = format(window.windowEnd, "MMMM", {
		locale: dateFnsLocale,
	});
	const monthLabel = isSameMonth(window.windowStart, window.windowEnd)
		? firstMonth
		: t("calendar.monthLabelJoin", { first: firstMonth, second: lastMonth });

	return (
		<section className={styles.section}>
			<div className={styles.header}>
				<h2 className={styles.title}>{t("overview.title")}</h2>
				<button
					type="button"
					className={styles.addBtn}
					aria-label={t("addEvent.ariaLabel")}
					onClick={onAddEvent}
				>
					<PlusIcon className={styles.plusIcon} width={20} height={20} />
				</button>
			</div>

			<div className={styles.monthRow}>
				<span className={styles.monthLabel}>{monthLabel}</span>
				<div className={styles.monthNav}>
					<button
						type="button"
						className={styles.navBtn}
						aria-label={t("calendar.prevTwoWeeks")}
						onClick={window.prev}
					>
						{/* Pitfall 1: ChevronLeftIcon is SEMANTICALLY "previous" — logical CSS handles RTL flip */}
						<ChevronLeftIcon className={styles.navIcon} />
					</button>
					<button
						type="button"
						className={styles.navBtn}
						aria-label={t("calendar.nextTwoWeeks")}
						onClick={window.next}
					>
						<ChevronRightIcon className={styles.navIcon} />
					</button>
				</div>
			</div>

			<EventLegend />

			<TwoWeekCalendar
				windowStart={window.windowStart}
				events={events ?? []}
				journeyOccurrences={journeyOccurrences}
				onDayTap={onDayTap}
				focusDayIso={focusDayIso}
				selectedDayIso={selectedDayIso}
			/>
		</section>
	);
}
